/**
 * API client — all backend requests go through here.
 * Automatically attaches Supabase JWT for authentication.
 *
 * The access token is pushed here by AuthContext whenever the Supabase
 * session changes, so API calls never need to call getSession() (which
 * deadlocks when invoked inside onAuthStateChange).
 */

const API_BASE = '/api/v1'

let _accessToken = null

export function setAccessToken(token) {
  _accessToken = token
}

function getAuthHeaders() {
  if (!_accessToken) {
    throw new Error('Not authenticated')
  }
  return {
    'Authorization': `Bearer ${_accessToken}`,
    'Content-Type': 'application/json',
  }
}

async function request(method, path, body = null) {
  const headers = getAuthHeaders()
  const opts = { method, headers }

  if (body && !(body instanceof FormData)) {
    opts.body = JSON.stringify(body)
  } else if (body instanceof FormData) {
    delete headers['Content-Type']
    opts.headers = headers
    opts.body = body
  }

  const resp = await fetch(`${API_BASE}${path}`, opts)

  if (!resp.ok) {
    const error = await resp.json().catch(() => ({ detail: resp.statusText }))
    throw new Error(error.detail || `API error: ${resp.status}`)
  }

  return resp.json()
}

export const api = {
  get: (path) => request('GET', path),
  post: (path, body) => request('POST', path, body),
  put: (path, body) => request('PUT', path, body),
  delete: (path) => request('DELETE', path),

  // File upload helper — type is auto-detected by the backend
  upload: async (orgId, file) => {
    const headers = getAuthHeaders()

    const formData = new FormData()
    formData.append('file', file)

    const resp = await fetch(`${API_BASE}/uploads/${orgId}`, {
      method: 'POST',
      headers: { 'Authorization': headers['Authorization'] },
      body: formData,
    })

    if (!resp.ok) {
      const error = await resp.json().catch(() => ({ detail: resp.statusText }))
      throw new Error(error.detail || 'Upload failed')
    }

    return resp.json()
  },

  // Investor Data upload helper
  uploadInvestorData: async (orgId, file) => {
    const headers = getAuthHeaders()

    const formData = new FormData()
    formData.append('file', file)

    const resp = await fetch(`${API_BASE}/investor-data/${orgId}/upload`, {
      method: 'POST',
      headers: { 'Authorization': headers['Authorization'] },
      body: formData,
    })

    if (!resp.ok) {
      const error = await resp.json().catch(() => ({ detail: resp.statusText }))
      throw new Error(error.detail || 'Investor data upload failed')
    }

    return resp.json()
  },
}
